import { Amplify } from 'aws-amplify'
import { post } from 'aws-amplify/api'
import { fetchAuthSession } from 'aws-amplify/auth'
import { 
  CalculationRequest, 
  CalculationResponse
} from './api-types'

const amplifyConfig = {
  Auth: {
    Cognito: {
      identityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || '',
      region: import.meta.env.VITE_AWS_REGION || 'us-east-2',
      
      allowGuestAccess: true
    }
  },
  API: {
    REST: {
      crewPayApi: {
        endpoint: import.meta.env.VITE_API_ENDPOINT || '',
        region: import.meta.env.VITE_AWS_REGION || 'us-east-2'
      }
    }
  }
}

if (typeof window !== 'undefined') {
  Amplify.configure(amplifyConfig)
}

// Single generic error message for all public-facing errors
const GENERIC_ERROR_MESSAGE = 'Service temporarily unavailable. Please try again later.'

export class ApiError extends Error {
  constructor(message: string = GENERIC_ERROR_MESSAGE) {
    super(message)
    this.name = 'ApiError'
  }
}

export class CrewPayApiClient {
  private apiName = 'crewPayApi'

  private async getGuestCredentials(): Promise<void> {
    try {
      const session = await fetchAuthSession()
      
      if (!session.credentials) {
        throw new Error('Authentication failed')
      }
    } catch (error) {
      // Log error for debugging (only in development)
      if (import.meta.env.DEV) {
        console.error('Authentication error:', error)
      }
      throw new ApiError()
    }
  }

  async calculatePay(requestData: CalculationRequest): Promise<CalculationResponse> {
    try {
      await this.getGuestCredentials()

      const restOperation = post({
        apiName: this.apiName,
        path: '/crew-pay/calculator',
        options: {
          body: requestData as Record<string, any>  // JSON serialization automatically
        }
      })

      const response = await restOperation.response
      
      const responseText = await response.body.text()
      let responseBody: CalculationResponse

      try {
        responseBody = JSON.parse(responseText) as CalculationResponse
      } catch (parseError) {
        // Log parsing error for debugging (only in development)
        if (import.meta.env.DEV) {
          console.error('Response parsing error:', parseError)
          console.error('Original response:', responseText)
        }
        throw new ApiError()
      }

      if (response.statusCode !== 200) {
        // Log API errors for debugging (only in development)
        if (import.meta.env.DEV) {
          console.error('API Error:', {
            status: response.statusCode,
            response: responseBody
          })
        }
        throw new ApiError()
      }

      if (!responseBody.metadata || !responseBody.current || !responseBody.projections) {
        // Log structure errors for debugging (only in development)
        if (import.meta.env.DEV) {
          console.error('Invalid response structure:', responseBody)
        }
        throw new ApiError()
      }

      return responseBody

    } catch (error) {      
      // Log all errors for debugging (only in development)
      if (import.meta.env.DEV) {
        console.error('Calculate pay error:', error)
      }
      
      // Always throw the same generic error regardless of the underlying issue
      if (error instanceof ApiError) {
        throw error
      }
      
      throw new ApiError()
    }
  }
}

export const apiClient = new CrewPayApiClient()